import { Trash2 } from "lucide-react";
import {
  defaultOperatorFor,
  operatorDefinition,
  operatorsFor,
  type FieldDefinition,
  type FilterCondition,
  type FilterOperator,
  type Json,
  type OperatorArity,
} from "@estelle/domain";

import { ValueInput } from "@/components/filters/ValueInput";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * What an already-picked value becomes under an operator of another arity.
 *
 * Switching "is" to "is any of" keeps the one value picked, as a list of one;
 * switching back keeps the first. Without it the choice is dropped on the
 * floor, or kept in a shape the new operator cannot show, and a clause that
 * looks filled in is rejected for a reason the screen does not explain. A
 * range has no single value to carry either way, so it starts over.
 */
function carryValue(
  value: Json | undefined,
  from: OperatorArity | undefined,
  to: OperatorArity,
): Json | undefined {
  if (to === "none") return undefined;
  if (from === to) return value;
  if (to === "many" && from === "one" && value !== undefined && value !== "") {
    return [value];
  }
  if (to === "one" && from === "many" && Array.isArray(value)) {
    return value[0];
  }
  return undefined;
}

/**
 * One `field operator value` clause.
 *
 * Changing the field resets the operator and clears the value: an operator
 * valid for a date is rarely valid for a tag, and carrying a stale value
 * across produces a clause that looks filled in and means nothing.
 */
export function ConditionRow({
  condition,
  fields,
  problem,
  onChange,
  onRemove,
}: {
  condition: FilterCondition;
  fields: FieldDefinition[];
  problem?: string;
  onChange: (condition: FilterCondition) => void;
  onRemove: () => void;
}) {
  const field = fields.find((f) => f.name === condition.field);
  const operators = field ? operatorsFor(field.type) : [];
  const definition = field
    ? operatorDefinition(field.type, condition.operator)
    : undefined;

  return (
    <div className="space-y-1">
      {/* Wraps rather than overflows: the field and the condition are 160px
          and 176px, which is wider than the popover on a phone. */}
      <div className="flex flex-wrap items-start gap-2">
        <Select
          value={condition.field}
          onValueChange={(name) => {
            const next = fields.find((f) => f.name === name);
            onChange({
              kind: "condition",
              field: name,
              operator: next
                ? defaultOperatorFor(next.type)
                : condition.operator,
            });
          }}
        >
          <SelectTrigger className="h-8 w-40 shrink-0">
            <SelectValue placeholder="Field" />
          </SelectTrigger>
          <SelectContent>
            {fields
              .filter((f) => f.filterable !== false)
              .map((f) => (
                <SelectItem key={f.name} value={f.name}>
                  {f.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Select
          value={condition.operator}
          onValueChange={(operator) => {
            const next = field
              ? operatorDefinition(field.type, operator as FilterOperator)
              : undefined;
            onChange({
              ...condition,
              operator: operator as FilterOperator,
              // An operator that takes no value must not keep one, or
              // validation rejects a clause the user cannot see is wrong.
              value: next
                ? carryValue(condition.value, definition?.arity, next.arity)
                : condition.value,
            });
          }}
        >
          <SelectTrigger className="h-8 w-44 shrink-0">
            <SelectValue placeholder="Condition" />
          </SelectTrigger>
          <SelectContent>
            {operators.map((operator) => (
              <SelectItem key={operator.operator} value={operator.operator}>
                {operator.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="min-w-40 flex-1">
          {field && definition ? (
            <ValueInput
              field={field}
              arity={definition.arity}
              value={condition.value}
              onChange={(value) => {
                onChange({ ...condition, value });
              }}
            />
          ) : null}
        </div>

        <Button
          size="icon-sm"
          variant="ghost"
          aria-label="Remove condition"
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>

      {problem ? (
        <p className="text-destructive pl-1 text-xs">{problem}</p>
      ) : null}
    </div>
  );
}
